// KÖZÖS „> Részletek" kibontó a lista-táblák ELSŐ oszlopához: egy toggle + alatta `címke: érték` sorok.
//
// MIÉRT KÖZÖS: ugyanez a minta MÁR HÁROM lista-oldalon kell (eszközök · üzenetek · jegyek), és az
// eseménynapló első oszlopa is ezt a nyelvet beszéli. A DRY-szabály a 2. előfordulásnál a közös rétegbe
// rendeli — a 3.-nál pedig már mérhető a szórás (a másolatokban eltért a chevron-méret, a betűméret és a
// panel bal szegélye). Egy komponensben tartva a kinézet nem tud elcsúszni.
//
// A MOBIL-kártya NEM ezt használja: ott a `DataTable` `cardExtraItems`-e adja ugyanezeket a párokat a kártya
// egyetlen „Részletek" rácsába (`CardDetailList`) — tudatos különbség, mert a kártyán már a kártya-fejléc
// kibontója a kapcsoló, és két egymás alatti kibontó félreérthető lenne.
import { ChevronRightIcon } from '@heroicons/react/20/solid';
import { createContext, useContext, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';

/**
 * „MOBIL-KÁRTYÁN VAGYUNK" jelzés — a `DataTable` kártya-ága állítja `true`-ra.
 *
 * MIÉRT KELL: a kártya-fejléc a `cardCell`-t rendereli, és ha egy lista azt NEM adja meg, a `cell` teljes
 * tartalma megy át — a benne lévő `DetailsExpander`-rel együtt. Így KÉT „Részletek" kapcsoló ült egy kártyán
 * (a kártyáé + a celláé), ami félreérthető (user-lelet: a belső jegy-listán; korábban az
 * üzenet-listán is ott volt). A `cardCell` továbbra is a kártya-fejléc TERVEZÉSÉNEK eszköze — ez a context
 * csak a VÉDŐHÁLÓ: a kibontó kártyán belül nem renderel kapcsolót, tehát az elfelejtett `cardCell` nem tud
 * néma felület-hibát okozni. A párok a kártyán a `cardExtraItems`-ből, EGY rácsba kerülnek.
 */
export const InCardContext = createContext(false);

/** Egy `címke: érték` pár a részlet-panelben. Az `value` `ReactNode`, mert badge/chip is előfordul. */
export interface DetailsRow {
  label: string;
  value: ReactNode;
  /** Stabil kulcs, ha a `label` NEM egyedi — ugyanaz a szerződés, mint a kártya-oldali `CardDetailItem`-é.
   *  Kell: az ügyvezető-történetben a címke maga a NÉV, és egy visszatérő ügyvezető kétszer is szerepelhet. */
  key?: string;
}

export interface DetailsExpanderProps {
  rows: DetailsRow[];
  /** A toggle felirata; alap: `common.details` („Részletek") — EGY kulcs az egész rendszerben. */
  label?: string;
  /** A toggle MELLETT megjelenő tartalom (pl. típus-badge) — a sor jobb oldalán, ugyanabban a flex-sorban. */
  trailing?: ReactNode;
  className?: string;
}

/**
 * A toggle CSAK akkor jelenik meg, ha van megjeleníthető sor — üres panelhez nem adunk kapcsolót. A `trailing`
 * ettől függetlenül renderel (badge sor nélkül is kell).
 */
export function DetailsExpander({
  rows,
  label,
  trailing,
  className,
}: DetailsExpanderProps): JSX.Element | null {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const inCard = useContext(InCardContext);
  if (rows.length === 0 && trailing === undefined) return null;
  // KÁRTYÁN: nincs kapcsoló (azt a kártya adja) — a `trailing` (jelvények) viszont KELL, az a sor állapota.
  if (inCard) return trailing === undefined ? null : <>{trailing}</>;
  return (
    <div className={cn('min-w-0', className)}>
      <div className="flex min-w-0 flex-wrap items-center gap-2">
        {rows.length > 0 && (
          <button
            type="button"
            onClick={(ev) => {
              // A sor-kattintás (megnyitás/navigáció) NEM sülhet el a kibontóra kattintva.
              ev.stopPropagation();
              setOpen((v) => !v);
            }}
            className="text-subtle hover:text-foreground focus-visible:ring-accent-600 -ml-0.5 flex items-center gap-1 rounded text-xs font-light transition-colors focus-visible:outline-none focus-visible:ring-2"
            aria-expanded={open}
          >
            <ChevronRightIcon
              className={cn('size-3.5 shrink-0 transition-transform', open && 'rotate-90')}
            />
            {label ?? t('common.details')}
          </button>
        )}
        {trailing}
      </div>
      {rows.length > 0 && open && (
        <dl className="text-muted-foreground mt-1 space-y-0.5 border-l-2 border-[color:var(--table-hairline)] pl-2.5 text-xs">
          {rows.map((r, i) => (
            <div key={r.key ?? `${r.label}-${String(i)}`} className="flex gap-1.5">
              <dt className="font-medium">{r.label}:</dt>
              <dd className="min-w-0 truncate">{r.value}</dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  );
}
